import React from "react";
import {
    DataGrid,
    gridFilteredSortedRowIdsSelector,
    gridVisibleColumnFieldsSelector,
    useGridApiContext,
    GridFooterContainer,
    GridPagination
} from '@mui/x-data-grid';
import Paper from '@mui/material/Paper';
import useAxios from '../../Hooks/useAxios';
import { Button, CircularProgress, Typography, Menu, MenuItem, Tooltip, Box } from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import PrintIcon from '@mui/icons-material/Print';
import DescriptionIcon from '@mui/icons-material/Description';
import TableChartIcon from '@mui/icons-material/TableChart';
import Grid from '@mui/material/Grid2';
import { GlobalContext } from "../../Context/GlobalContext";
import * as XLSX from 'xlsx';
import FolderIcon from '@mui/icons-material/Folder';
import FileModal from "../FileModal/FileModal";

const departmentColors = {
  "TALENTO": "#4CAF50",                       // green
  "RRHH": "#8BC34A",                          // light green
  "AES": "#2196F3",                           // blue
  "SERVICIOS GENERALES": "#795548",           // brown
  "ADM. OPERACIONES": "#FF9800",              // orange
  "MANAGER": "#3F51B5",                       // indigo
  "EHS": "#F44336",                           // red
  "GESTION DOCUMENTAL": "#607D8B",            // blue grey
  "SERVICIOS OPERACIONES": "#009688",         // teal
  "FORMACIONES & ALSTOM UNIVERSITY": "#9C27B0", // purple
  "FINANZAS": "#FFC107",                      // amber (yellow)
  "DEFAULT": "#9E9E9E"                        // grey
};

/**
 * Custom toolbar component that preserves pagination and adds an export menu.
 * @returns {JSX.Element} The rendered footer component.
 */
const CustomToolbar = () => {
    const apiRef = useGridApiContext();
    const [anchorEl, setAnchorEl] = React.useState(null);
    const open = Boolean(anchorEl);

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };

    /**
     * Triggers the native CSV export from DataGrid API.
     */
    const handleExportCSV = () => {
        handleClose();
        setTimeout(() => {
            apiRef.current.exportDataAsCsv({
                fields: gridVisibleColumnFieldsSelector(apiRef).filter(
                    field => field !== '__check__' && field !== 'actions'
                )
            });
        }, 150);
    };

    /**
     * Triggers the native Print functionality from DataGrid API.
     */
    const handlePrint = () => {
        handleClose();
        setTimeout(() => {
            apiRef.current.exportDataAsPrint({
                hideToolbar: true,
                hideFooter: true
            });
        }, 150);
    };

    /**
     * Extracts data and triggers Excel download.
     */
    const handleExportExcel = () => {
        handleClose();
        setTimeout(() => {
            const filteredSortedRowIds = gridFilteredSortedRowIdsSelector(apiRef);
            const visibleColumns = gridVisibleColumnFieldsSelector(apiRef);

            // Eliminamos solo campos internos de control, permitiendo que 'subprocess' (Status) se exporte
            const fieldsToExport = visibleColumns.filter(field => field !== '__check__' && field !== 'actions');

            const dataToExport = filteredSortedRowIds.map((id) => {
                const row = apiRef.current.getRow(id);
                const exportRow = {};
                fieldsToExport.forEach((field) => {
                    exportRow[field] = row[field];
                });
                return exportRow;
            });

            const worksheet = XLSX.utils.json_to_sheet(dataToExport);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, "Processes");
            XLSX.writeFile(workbook, "Process_Report.xlsx");
        }, 150);
    };

    return (
        <GridFooterContainer sx={{ paddingX: "10px" }}>
            <Box sx={{ flexGrow: 1 }} />
            <GridPagination />
            <Tooltip title="Exportar">
                <Button
                    id="export-button"
                    aria-controls={open ? 'export-menu' : undefined}
                    aria-haspopup="true"
                    aria-expanded={open ? 'true' : undefined}
                    onClick={handleClick}
                    sx={{
                        height: "48px",
                        width: "48px",
                        borderRadius: "50%",
                        minWidth: "48px",
                        padding: 0,
                        color: "text.secondary",
                        "&:hover": {
                            backgroundColor: "rgba(0, 0, 0, 0.04)"
                        }
                    }}
                >
                    <SaveAltIcon fontSize="medium" />
                </Button>
            </Tooltip>
            <Menu
                id="export-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                MenuListProps={{ 'aria-labelledby': 'export-button' }}
            >
                <MenuItem onClick={handleExportCSV}>
                    <DescriptionIcon sx={{ marginRight: 1 }} fontSize="small" /> Exportar a CSV
                </MenuItem>
                <MenuItem onClick={handleExportExcel}>
                    <TableChartIcon sx={{ marginRight: 1 }} fontSize="small" /> Exportar a Excel
                </MenuItem>
                <MenuItem onClick={handlePrint}>
                    <PrintIcon sx={{ marginRight: 1 }} fontSize="small" /> Imprimir
                </MenuItem>
            </Menu>
        </GridFooterContainer>
    );
};

/**
 * A table component that displays a list of processes and their details. It fetches data from an API,
 * renders the data in a Material-UI `DataGrid`, and provides functionality to open a modal for subprocess details.
 *
 * @component
 * @returns {JSX.Element} The rendered process table component.
 */
const ProcessTable = () => {
    const { data, isLoading, error } = useAxios("processSummaries");
    const { setModalOpen, setModalData, setAltaCentroModalOpen, setAltaCentroModalData, setAlertBarSeverity, setAlertBarMessage, setAlertBarOpen } = React.useContext(GlobalContext);
    const [tableData, setTableData] = React.useState([]);
    const [openModal, setOpenModal] = React.useState(false);
    const [selectedRow, setSelectedRow] = React.useState(null);

    /**
     * Fetches process summaries from the API and updates the table data. Displays an error message if the request fails.
     */
    React.useEffect(() => {
        if (!isLoading) {
            if (error) {
                setAlertBarSeverity("error");
                setAlertBarMessage(error);
                setAlertBarOpen(true);
            } else  {
                setTableData(data || []);
            }
        }
    }, [isLoading, data, error]);

    /**
     * Handles opening a modal to display details of a specific subprocess.
     *
     * @param {string} subprocessId - The ID of the subprocess to display.
     */
    const handleOpen = (subprocessId) => {
        setModalData({id: subprocessId});
        setModalOpen(true);
    };

    /**
     * Defines the columns for the DataGrid, including custom rendering for the "Status" and "Schedule" columns.
     */
    const columns = [
        { field: 'process', headerName: 'Process', description: 'Process', flex: 1, minWidth: 140, headerAlign: "center" },
        { field: 'subprocess', headerName: 'Status',  description: 'Status', flex: 1, minWidth: 140, headerAlign: "center",
            renderCell: (item) => (
                <Grid container spacing={0}>
                    <Grid size={11} sx={{overflow: "hidden", textWrap: "nowrap", textOverflow: "ellipsis"}}>
                            {item.value}
                    </Grid>
                    <Grid size={1} sx={{ height: "52px"}}>
                        <Button onClick={handleOpen.bind(null, item.row.subprocessId)} sx={{width: "30px", minWidth: "30px", borderRadius: "100%", marginLeft: "-10px"}}>
                            <ExpandMoreIcon color="disabled" onClick={handleOpen.bind(null, item.row.subprocessId)}/>
                        </Button>
                    </Grid>
                </Grid>
            )
         },
        { field: 'responsible', headerName: 'Responsible', description: 'Responsible', flex: 1, minWidth: 140, headerAlign: "center" },
        { field: 'schedule', headerName: 'Schedule', description: 'Schedule', type: 'number', flex: 1, minWidth: 140, headerAlign: "center",
            renderCell: (params) => (
                <Typography color={params.value >= 0? "#63cf63" : "#d97575"} align="center" sx={{position: "relative", top: "25%"}}>
                    {params.value}
                </Typography>
            )
         },
        {
          field: "department",
          headerName: "Department",
          renderCell: (params) => {
            // 🔐 Normalize ONLY for lookup
            const normalizedKey = params.value
              ?.normalize("NFD")                  // remove accents
              .replace(/[\u0300-\u036f]/g, "")
              .toUpperCase();
            console.log("params.row.departmentColor:", params.row.departmentColor)
            const color = params.row.departmentColor;

           //const color =
           //  departmentColors[normalizedKey] || departmentColors.DEFAULT;

            // 🎨 Optional: better contrast for light colors
            const textColor = color === "#FFC107" ? "black" : "white";

            return (
              <span
                style={{
                  backgroundColor: color,
                  color: textColor,
                  padding: "4px 8px",
                  borderRadius: "8px",
                  fontWeight: "500",
                  display: "inline-block"
                }}
              >
                {/* ✅ ORIGINAL VALUE (unchanged) */}
                {params.value}
              </span>
            );
          }
        },
        { field: 'targetEmployeeCenter', headerName: 'Center', description: 'Employee Center', flex: 1, minWidth: 140, headerAlign: "center" },
        { field: 'targetEmployeePositionGeneric', headerName: 'Generic Position', description: 'Generic Employee Position', flex: 1, minWidth: 140, headerAlign: "center" },
        { field: 'targetEmployeePositionSpecific', headerName: 'Specific Position', description: 'Specific Employee Position', flex: 1, minWidth: 140, headerAlign: "center" },
        { field: 'alpsCode', headerName: 'ALPS code', description: 'ALPS code', flex: 1, minWidth: 140, headerAlign: "center" },
        { field: 'targetEmployeeName', headerName: 'Employee name', description: 'Employee name', flex: 1, minWidth: 140, headerAlign: "center" },
        { field: 'targetEmployeeSurname', headerName: 'Employee surname', description: 'Employee surname', flex: 1, minWidth: 140, headerAlign: "center" },
        {
          field: 'files',
          headerName: '',
          sortable: false,
          filterable: false,
          width: 80,
          align: 'center',
          renderCell: (params) => {
            return (
              <FolderIcon
                sx={{ cursor: 'pointer', color: '#1976d2' }}
                onClick={() => {
                  setSelectedRow(params.row);
                  setOpenModal(true);
                }}
              />
            );
          }
        }
    ];

    /**
     * Maps the fetched data into rows for the DataGrid.
     */
    const rows = [...tableData.map(row => {
        return {
            id: `${row.processId}_${row.subprocessId}`,
            process: `${row.process} ${row.processId}`,
            subprocessId: row.subprocessId,
            subprocess: row.subprocess,
            responsible: row.responsible,
            schedule: row.schedule,
            department: row.department,
            //center: row.center,
            targetEmployeeCenter: row.targetEmployeeCenter,
            //positionGeneric: row.positionGeneric,
            targetEmployeePositionGeneric: row.targetEmployeePositionGeneric,
            //positionSpecific: row.positionSpecific,
            targetEmployeePositionSpecific: row.targetEmployeePositionSpecific,
            alpsCode: row.alpsCode,
            targetEmployeeName: row.targetEmployeeName,
            targetEmployeeSurname: row.targetEmployeeSurname,
            departmentColor: row.departmentColor


        };
    })]

    /**
     * Calculates the optimal page size for the DataGrid based on the window height.
     * * @param {Array} sizeOptions - An array of possible page sizes.
     * @returns {number} The calculated page size.
     */
    const calculateSize = (sizeOptions) => {    
        const maxRows = Math.round(window.innerHeight / 52) - 4;
        return sizeOptions.filter(size => size >= maxRows).shift() || 10;
    }

    const sizeOptions = [5, 10, 15, 20];
    const paginationModel = { page: 0, pageSize: calculateSize(sizeOptions) };
    
    return (
        <>
            <Paper sx={{ minWidth: 702, borderRadius: "23px"}}>
                <DataGrid
                    rows={rows}
                    columns={columns}
                    initialState={{ pagination: { paginationModel } }}
                    pageSizeOptions={sizeOptions}
                    slots={{
                        footer: CustomToolbar,
                    }}
                    sx={{
                        textAlign: "left",
                        borderRadius: "23px",
                        boxShadow: '3px 11px 7px -6px rgba(161, 156, 161, 1)',
                        height: "86vh"
                    }}
                    localeText={{
                        noRowsLabel: isLoading? <><CircularProgress size="1rem" sx={{marginRight: "4px"}} />Loading...</> : error? error : "",
                    }}
                />
            </Paper>
            <FileModal
                open={openModal}
                onClose={() => setOpenModal(false)}
                rowData={selectedRow}
            />
        </>
    );
}

export default ProcessTable;